import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { createTrip, getTrips, getCurrentTrip, deleteTrip, updateTrip } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plane, Plus, MapPin, Clock, Calendar, Trash2, Globe, Sun, Sparkles, Pencil, Check } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const timezones = [
    { value: 'UTC', label: 'UTC (0)', offset: 0 },
    { value: 'Europe/Berlin', label: 'Berlin (CET)', offset: 1 },
    { value: 'Europe/London', label: 'London (GMT)', offset: 0 },
    { value: 'America/New_York', label: 'New York (EST)', offset: -5 },
    { value: 'America/Los_Angeles', label: 'Los Angeles (PST)', offset: -8 },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)', offset: 9 },
    { value: 'Asia/Dubai', label: 'Dubai (GST)', offset: 4 },
    { value: 'Australia/Sydney', label: 'Sydney (AEST)', offset: 11 },
];

export default function Travel() {
    const [trips, setTrips] = useState([]);
    const [currentTrip, setCurrentTrip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingEntry, setEditingEntry] = useState(null);
    const [tripForm, setTripForm] = useState({
        destination: '',
        start_date: '',
        end_date: '',
        home_timezone: 'Europe/Berlin',
        destination_timezone: 'UTC',
        timezone_diff_hours: 0,
        notes: ''
    });

    useEffect(() => {
        loadTrips();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadTrips = async () => {
        try {
            const [tripsRes, currentRes] = await Promise.all([
                getTrips(),
                getCurrentTrip()
            ]);
            setTrips(tripsRes.data);
            setCurrentTrip(currentRes.data);
        } catch (error) {
            console.error('Failed to load trips:', error);
            toast.error('Fehler beim Laden der Reisen');
        } finally {
            setLoading(false);
        }
    };

    const handleAddTrip = async (e) => {
        e.preventDefault();
        try {
            const homeOffset = timezones.find(t => t.value === tripForm.home_timezone)?.offset || 0;
            const destOffset = timezones.find(t => t.value === tripForm.destination_timezone)?.offset || 0;
            
            await createTrip({
                ...tripForm,
                start_date: new Date(tripForm.start_date).toISOString(),
                end_date: new Date(tripForm.end_date).toISOString(),
                timezone_diff_hours: destOffset - homeOffset
            });
            toast.success('Reise hinzugefügt!');
            setDialogOpen(false);
            setTripForm({
                destination: '',
                start_date: '',
                end_date: '',
                home_timezone: 'Europe/Berlin',
                destination_timezone: 'UTC',
                timezone_diff_hours: 0,
                notes: ''
            });
            loadTrips();
        } catch (error) {
            toast.error('Fehler beim Erstellen');
        }
    };

    const handleDeleteTrip = async (id) => {
        try {
            await deleteTrip(id);
            toast.success('Reise gelöscht');
            loadTrips();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const handleEditTrip = (trip) => {
        const startDate = trip.start_date ? trip.start_date.split('T')[0] : '';
        const endDate = trip.end_date ? trip.end_date.split('T')[0] : '';
        setEditingEntry({
            ...trip,
            start_date: startDate,
            end_date: endDate
        });
        setEditDialogOpen(true);
    };

    const handleUpdateTrip = async () => {
        if (!editingEntry) return;
        try {
            const homeOffset = timezones.find(t => t.value === editingEntry.home_timezone)?.offset || 0;
            const destOffset = timezones.find(t => t.value === editingEntry.destination_timezone)?.offset || 0;
            
            await updateTrip(editingEntry.id, {
                destination: editingEntry.destination,
                start_date: new Date(editingEntry.start_date).toISOString(),
                end_date: new Date(editingEntry.end_date).toISOString(),
                home_timezone: editingEntry.home_timezone,
                destination_timezone: editingEntry.destination_timezone,
                timezone_diff_hours: destOffset - homeOffset,
                notes: editingEntry.notes || ''
            });
            toast.success('Reise aktualisiert');
            setEditDialogOpen(false);
            setEditingEntry(null);
            loadTrips();
        } catch (error) {
            toast.error('Fehler beim Speichern');
        }
    };

    const getJetlagTips = (diff) => {
        const absDiff = Math.abs(diff);
        if (absDiff <= 2) return ['Minimaler Jetlag - Genieße deine Reise!'];
        if (absDiff <= 5) return [
            'Früh Sonnenlicht am Morgen suchen',
            'Koffein nur bis Mittag',
            'Leichte Mahlzeiten am Abend'
        ];
        return [
            `Anpassung dauert ca. ${absDiff} Tage`,
            diff > 0 ? 'Abends Licht suchen, morgens meiden' : 'Morgens Licht suchen, abends meiden',
            'Melatonin kann helfen (abends Zielzeit)',
            'Viel Wasser trinken',
            'Leichte Bewegung hilft bei der Anpassung'
        ];
    };

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="travel-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto space-y-6"
            >
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-display text-2xl lg:text-3xl font-bold flex items-center gap-3">
                            <Plane className="w-8 h-8 text-sky-500" />
                            Reisen
                        </h1>
                        <p className="text-muted-foreground mt-1">Verwalte deine Reisen und Jetlag</p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-full gap-2" data-testid="add-trip-btn">
                                <Plus className="w-4 h-4" />
                                Reise
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle>Neue Reise planen</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleAddTrip} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="destination">Ziel</Label>
                                    <Input
                                        id="destination"
                                        value={tripForm.destination}
                                        onChange={(e) => setTripForm({ ...tripForm, destination: e.target.value })}
                                        placeholder="z.B. New York, Tokyo..."
                                        required
                                        data-testid="trip-destination-input"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="start_date">Abreise</Label>
                                        <Input
                                            id="start_date"
                                            type="date"
                                            value={tripForm.start_date}
                                            onChange={(e) => setTripForm({ ...tripForm, start_date: e.target.value })}
                                            required
                                            data-testid="trip-start-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="end_date">Rückkehr</Label>
                                        <Input
                                            id="end_date"
                                            type="date"
                                            value={tripForm.end_date}
                                            onChange={(e) => setTripForm({ ...tripForm, end_date: e.target.value })}
                                            required
                                            data-testid="trip-end-input"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Heimat-Zeitzone</Label>
                                        <select
                                            className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            value={tripForm.home_timezone}
                                            onChange={(e) => setTripForm({ ...tripForm, home_timezone: e.target.value })}
                                            data-testid="trip-home-tz-select"
                                        >
                                            {timezones.map((tz) => (
                                                <option key={tz.value} value={tz.value}>{tz.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Ziel-Zeitzone</Label>
                                        <select
                                            className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            value={tripForm.destination_timezone}
                                            onChange={(e) => setTripForm({ ...tripForm, destination_timezone: e.target.value })}
                                            data-testid="trip-dest-tz-select"
                                        >
                                            {timezones.map((tz) => (
                                                <option key={tz.value} value={tz.value}>{tz.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="notes">Notizen</Label>
                                    <Input
                                        id="notes"
                                        value={tripForm.notes}
                                        onChange={(e) => setTripForm({ ...tripForm, notes: e.target.value })}
                                        placeholder="Flugdetails, Hotel..."
                                        data-testid="trip-notes-input"
                                    />
                                </div>
                                <Button type="submit" className="w-full" data-testid="submit-trip-btn">
                                    Reise erstellen
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Current Trip */}
                {currentTrip && (
                    <Card className="module-travel border-2 border-sky-300 dark:border-sky-700">
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Plane className="w-5 h-5 text-sky-500 animate-pulse" />
                                    Aktive Reise
                                </CardTitle>
                                <Badge className="bg-sky-500">Unterwegs</Badge>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 rounded-2xl bg-sky-100 dark:bg-sky-900/50 flex items-center justify-center">
                                    <MapPin className="w-8 h-8 text-sky-500" />
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold">{currentTrip.destination}</h3>
                                    <p className="text-muted-foreground">
                                        {format(parseISO(currentTrip.start_date), 'dd. MMM', { locale: de })} - {format(parseISO(currentTrip.end_date), 'dd. MMM yyyy', { locale: de })}
                                    </p>
                                </div>
                            </div>
                            
                            {currentTrip.timezone_diff_hours !== 0 && (
                                <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Clock className="w-5 h-5 text-amber-600" />
                                        <span className="font-semibold text-amber-800 dark:text-amber-200">
                                            Jetlag-Tipps ({currentTrip.timezone_diff_hours > 0 ? '+' : ''}{currentTrip.timezone_diff_hours}h)
                                        </span>
                                    </div>
                                    <ul className="space-y-2">
                                        {getJetlagTips(currentTrip.timezone_diff_hours).map((tip) => (
                                            <li key={`tip-${tip.slice(0, 32)}`} className="flex items-start gap-2 text-sm">
                                                <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                                <span>{tip}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Trip List */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Alle Reisen</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {trips.length === 0 ? (
                            <p className="text-center text-muted-foreground py-8">
                                Noch keine Reisen geplant. Plane deine erste Reise!
                            </p>
                        ) : (
                            trips.map((trip) => {
                                const startDate = parseISO(trip.start_date);
                                const endDate = parseISO(trip.end_date);
                                const now = new Date();
                                const isActive = now >= startDate && now <= endDate;
                                const isPast = now > endDate;
                                
                                return (
                                    <motion.div
                                        key={trip.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`flex items-center gap-4 p-4 rounded-xl ${isActive ? 'module-travel border-2 border-sky-300' : 'bg-secondary/50'} ${isPast ? 'opacity-60' : ''}`}
                                    >
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isActive ? 'bg-sky-100 dark:bg-sky-900/50' : 'bg-secondary'}`}>
                                            {isPast ? (
                                                <Globe className="w-6 h-6 text-muted-foreground" />
                                            ) : (
                                                <Plane className={`w-6 h-6 ${isActive ? 'text-sky-500' : 'text-muted-foreground'}`} />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="font-semibold text-sm">{trip.destination || 'Unbekannt'}</p>
                                                {isActive && <Badge className="bg-sky-500 text-xs">Aktiv</Badge>}
                                                {isPast && <Badge variant="secondary" className="text-xs">Vergangen</Badge>}
                                            </div>
                                            <p className="text-sm text-muted-foreground">
                                                {format(startDate, 'dd.MM.yy', { locale: de })} - {format(endDate, 'dd.MM.yy', { locale: de })}
                                            </p>
                                        </div>
                                        {trip.timezone_diff_hours !== 0 && (
                                            <Badge variant="outline" className="gap-1">
                                                <Clock className="w-3 h-3" />
                                                {trip.timezone_diff_hours > 0 ? '+' : ''}{trip.timezone_diff_hours}h
                                            </Badge>
                                        )}
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleEditTrip(trip)}
                                            className="text-muted-foreground hover:text-foreground"
                                            data-testid={`edit-trip-${trip.id}`}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDeleteTrip(trip.id)}
                                            className="text-destructive hover:text-destructive"
                                            data-testid={`delete-trip-${trip.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </motion.div>
                                );
                            })
                        )}
                    </CardContent>
                </Card>
            </motion.div>

            {/* Edit Trip Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Pencil className="w-5 h-5" />
                            Reise bearbeiten
                        </DialogTitle>
                    </DialogHeader>
                    {editingEntry && (
                        <div className="space-y-4">
                            <div className="space-y-2">
                                <Label>Ziel</Label>
                                <Input
                                    value={editingEntry.destination}
                                    onChange={(e) => setEditingEntry({ ...editingEntry, destination: e.target.value })}
                                    placeholder="z.B. New York, Tokyo..."
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Abreise</Label>
                                    <Input
                                        type="date"
                                        value={editingEntry.start_date}
                                        onChange={(e) => setEditingEntry({ ...editingEntry, start_date: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Rückkehr</Label>
                                    <Input
                                        type="date"
                                        value={editingEntry.end_date}
                                        onChange={(e) => setEditingEntry({ ...editingEntry, end_date: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Heimat-Zeitzone</Label>
                                    <select
                                        className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        value={editingEntry.home_timezone}
                                        onChange={(e) => setEditingEntry({ ...editingEntry, home_timezone: e.target.value })}
                                    >
                                        {timezones.map((tz) => (
                                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label>Ziel-Zeitzone</Label>
                                    <select
                                        className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                                        value={editingEntry.destination_timezone}
                                        onChange={(e) => setEditingEntry({ ...editingEntry, destination_timezone: e.target.value })}
                                    >
                                        {timezones.map((tz) => (
                                            <option key={tz.value} value={tz.value}>{tz.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label>Notizen</Label>
                                <Input
                                    value={editingEntry.notes || ''}
                                    onChange={(e) => setEditingEntry({ ...editingEntry, notes: e.target.value })}
                                    placeholder="Flugdetails, Hotel..."
                                />
                            </div>
                            <Button onClick={handleUpdateTrip} className="w-full gap-2" data-testid="save-edit-trip-btn">
                                <Check className="w-4 h-4" />
                                Speichern
                            </Button>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
